import { useQuery, useQueries } from "@tanstack/react-query";
import { NoteDto } from "../../api-client";
import { useApi } from "../../api/ApiProvider";
import { notesKeys } from "./notes.keys";

export const usePatientNotes = (
  patientId: number | string | undefined,
  enabled: boolean = true
) => {
  const { notes } = useApi();
  const normalizedPatientId = patientId ? patientId.toString() : "";

  return useQuery<NoteDto[]>({
    queryKey: normalizedPatientId
      ? notesKeys.patientNotes.detail(normalizedPatientId)
      : notesKeys.patientNotes.all(),
    queryFn: async () => {
      if (!normalizedPatientId) return [] as NoteDto[];
      const result = await notes.getPatientNotes(normalizedPatientId);
      return Array.isArray(result) ? result : ([] as NoteDto[]);
    },
    enabled: enabled && !!normalizedPatientId,
    staleTime: 0,
    refetchOnMount: true,
    meta: { errorMessage: "Patientenhinweise konnten nicht geladen werden" },
  });
};

export const useAppointmentNotes = (
  appointmentId: string | undefined,
  enabled: boolean = true
) => {
  const { notes } = useApi();
  const normalizedAppointmentId = appointmentId || "";

  return useQuery<NoteDto[]>({
    queryKey: normalizedAppointmentId
      ? notesKeys.appointmentNotes.detail(normalizedAppointmentId)
      : notesKeys.appointmentNotes.all(),
    queryFn: async () => {
      if (!normalizedAppointmentId) return [] as NoteDto[];
      const result = await notes.getAppointmentNotes(normalizedAppointmentId);
      return Array.isArray(result) ? result : ([] as NoteDto[]);
    },
    enabled: enabled && !!normalizedAppointmentId,
    staleTime: 0,
    refetchOnMount: true,
    meta: { errorMessage: "Hinweise zum Termin konnten nicht geladen werden" },
  });
};

/**
 * Fetch notes for multiple patients in parallel using useQueries.
 * Each patient's notes are cached individually under notesKeys.patientNotes.detail(patientId).
 */
export const useMultiplePatientNotes = (patientIds: (string | number)[]) => {
  const { notes } = useApi();

  const queries = useQueries({
    queries: patientIds.map((pid) => {
      const normalizedId = pid.toString();
      return {
        queryKey: notesKeys.patientNotes.detail(normalizedId),
        queryFn: async () => {
          const result = await notes.getPatientNotes(normalizedId);
          return Array.isArray(result) ? result : ([] as NoteDto[]);
        },
        enabled: !!normalizedId,
        staleTime: 0,
        refetchOnMount: true,
      };
    }),
  });

  // Build a map of patientId -> notes array
  const notesByPatientId: Record<string, NoteDto[]> = {};
  patientIds.forEach((pid, index) => {
    notesByPatientId[pid.toString()] = queries[index]?.data ?? [];
  });

  const isLoading = queries.some((q) => q.isLoading);

  return { notesByPatientId, isLoading };
};

/**
 * Fetch notes for multiple appointments in parallel using useQueries.
 * Undefined means the query has not returned yet, so callers can preserve cached flags.
 */
export const useMultipleAppointmentNotes = (appointmentIds: string[]) => {
  const { notes } = useApi();
  const validAppointmentIds = Array.from(
    new Set(appointmentIds.filter((appointmentId) => !!appointmentId && appointmentId.includes("-")))
  );

  const queries = useQueries({
    queries: validAppointmentIds.map((appointmentId) => ({
      queryKey: notesKeys.appointmentNotes.detail(appointmentId),
      queryFn: async () => {
        const result = await notes.getAppointmentNotes(appointmentId);
        return Array.isArray(result) ? result : ([] as NoteDto[]);
      },
      enabled: !!appointmentId,
      staleTime: 0,
      refetchOnMount: true,
    })),
  });

  const notesByAppointmentId: Record<string, NoteDto[] | undefined> = {};
  validAppointmentIds.forEach((appointmentId, index) => {
    notesByAppointmentId[appointmentId] = queries[index]?.data;
  });

  const isLoading = queries.some((query) => query.isLoading);

  return { notesByAppointmentId, isLoading };
};
